/**
 * Trigger a browser download for a Blob. Uses an object URL + synthetic
 * anchor click; revokes the URL shortly after to free memory.
 */
export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/**
 * Feature-check whether the current browser can share PDF files via the
 * Web Share API. iOS Safari 15+ and modern Android Chrome return true;
 * desktop browsers and older iOS return false.
 */
export function canSharePdfFiles() {
  if (typeof navigator === "undefined" || !navigator.canShare) return false;
  try {
    const probe = new File([""], "probe.pdf", { type: "application/pdf" });
    return navigator.canShare({ files: [probe] });
  } catch (e) {
    return false;
  }
}

/**
 * Heuristic for a touch-primary device (phone / tablet), where the native
 * share sheet is the better delivery path (iOS "Save to Files" + Messages /
 * WhatsApp / AirDrop). Laptops and desktops — including touchscreen laptops
 * driven by a mouse — report a fine primary pointer + hover and return false,
 * so they get a plain download (with an optional Share). Used to decide
 * Send-only (mobile) vs Download + Share (desktop). Web Share with files is
 * also available on Windows Chrome/Edge, but on a laptop it opens the OS
 * share sheet (Nearby sharing / link-to-phone) instead of saving the file —
 * which is why capability alone (canSharePdfFiles) isn't the right signal.
 */
export function isLikelyMobile() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
}

/**
 * Share a PDF via the native share sheet when supported, otherwise fall
 * back to a direct download. MUST be invoked synchronously inside a user
 * gesture handler with the blob already in hand — any `await` between the
 * tap and navigator.share() voids the user-activation token on iOS Safari
 * and the share call throws NotAllowedError.
 *
 * Behavior:
 *   - canShare({ files }) supported → open native share sheet
 *     - user picks a target → file goes there
 *     - user cancels (AbortError) → no-op (do NOT fall back to download;
 *       that would dump a file/previewer after they explicitly cancelled)
 *     - share throws any other error → fall back to triggerBlobDownload
 *   - canShare not supported → triggerBlobDownload (existing behavior)
 */
export async function shareOrDownloadPdf(blob, filename) {
  const file = new File([blob], filename, { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename });
    } catch (err) {
      if (err && err.name === "AbortError") return;
      triggerBlobDownload(blob, filename);
    }
    return;
  }
  triggerBlobDownload(blob, filename);
}
